import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAdminCreateOrganization } from "@/services/adminService";

const createOrganizationSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  contactEmail: z.string().email("Invalid email address").optional(),
  contactPersonName: z.string().optional(),
  contactPhone: z.string().optional(),
  defaultProjectArea: z.string().optional(),
  defaultProjectDescription: z.string().optional(),
  defaultProjectType: z.string().optional(),
  showAmaCodesDefault: z.boolean().default(false),
});

type CreateOrganizationFormData = z.infer<typeof createOrganizationSchema>;

interface CreateOrganizationFormProps {
  onSuccess?: (orgId: string) => void;
}

export const CreateOrganizationForm = ({ onSuccess }: CreateOrganizationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const form = useForm<CreateOrganizationFormData>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: "",
      contactEmail: "",
      contactPersonName: "",
      contactPhone: "",
      defaultProjectArea: "",
      defaultProjectDescription: "",
      defaultProjectType: "",
      showAmaCodesDefault: false,
    },
  });

  const createOrgMutation = useAdminCreateOrganization();

  const onSubmit = async (data: CreateOrganizationFormData) => {
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const created = await createOrgMutation.mutateAsync({
        ...data,
        name: data.name!, // Guaranteed by schema validation
      });

      form.reset();

      // Call onSuccess callback if provided (for modal usage)
      if (onSuccess) {
        onSuccess(created.id);
        return;
      }

      // Only show inline success message when not used in modal
      setSubmitResult({
        type: "success",
        message: `Organization "${data.name}" created successfully! ID: ${created.id}`,
      });
    } catch (error) {
      setSubmitResult({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create organization",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {submitResult && (
        <Alert
          className={
            submitResult.type === "success"
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          {submitResult.type === "success" ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription
            className={submitResult.type === "success" ? "text-green-800" : "text-red-800"}
          >
            {submitResult.message}
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Construction AB" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contactPersonName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Person Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="contact@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="contactPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Phone</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+46 8 123 45 67" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Project Defaults</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="defaultProjectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Project Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Residential Construction" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultProjectArea"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Project Area</FormLabel>
                    <FormControl>
                      <Input placeholder="Stockholm" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="defaultProjectDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Project Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Standard project description template..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? "Creating Organization..." : "Create Organization"}
          </Button>
        </form>
      </Form>
    </div>
  );
};
